<template>
  <div class="col-lg-9 col-md-12 p-0 border m-auto" ref="container">
    <canvas :width="width" :height="height" ref="canvas"></canvas>
  </div>
</template>
<script>
import Konva from 'konva'

export default {
  name: 'SignDrawCanvas',
  props: {
    allowDrawing: {
      type: Object,
      required: true
    },
    drawMode: {
      type: String,
      required: true
    },
    penColor: {
      type: String,
      required: true
    }
  },
  emits: ['sendSignalingDraw', 'registDrawnImage'],
  data () {
    return {
      width: 200,
      height: 200,
      stage: null,
      canvas: null,
      context: null,
      drawingLayer: null,
      drawingScope: null,
      lastPointerPosition: {},
      localPos: { x: 0, y: 0 },
      pos: null,
      isPaint: false
    }
  },

  watch: {
    penColor (newVal) {
      if (!this.context) return
      this.context.strokeStyle = newVal
    },

    // allowDrawing の中の isOperationPossible を見たいので deep + immediate
    allowDrawing: {
      deep: true,
      immediate: true,
      handler (newVal) {
        this.updateDrawingListeners(!!newVal?.isOperationPossible)
      }
    }
  },
  mounted () {
    const computedStyle = window.getComputedStyle(document.getElementById('content-area-inner'))
    let baseWidth =
      document.getElementById('content-area-inner').clientWidth -
      (parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight))
    baseWidth -= 2 // border 2px

    this.width = window.innerWidth >= 992 ? (baseWidth / 12) * 9 : baseWidth
    window.addEventListener('resize', this.calculateWidth)

    const container = this.$refs.container
    this.stage = new Konva.Stage({
      container,
      width: this.width,
      height: this.height
    })
    this.drawingLayer = new Konva.Layer()
    this.stage.add(this.drawingLayer)

    this.canvas = this.$refs.canvas
    this.drawingScope = new Konva.Image({
      image: this.canvas,
      x: 0,
      y: 0,
      stroke: '',
      fill: '#FFF'
    })
    this.drawingLayer.add(this.drawingScope)
    this.stage.draw()

    this.context = this.canvas.getContext('2d')
    this.context.strokeStyle = this.penColor || '#000000'
    this.context.lineJoin = 'round'

    // mounted 時点の allowDrawing 状態でイベント反映（watch immediate でも動きますが念のため）
    this.updateDrawingListeners(!!this.allowDrawing?.isOperationPossible)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.calculateWidth)
    this.updateDrawingListeners(false)
    // 念のため Konva インスタンス破棄
    if (this.stage) {
      this.stage.destroy()
      this.stage = null
    }
  },
  methods: {
    updateDrawingListeners (enabled) {
      if (!this.stage || !this.drawingScope) return

      // 二重登録防止のため先に解除してから必要なら付け直す
      this.drawingScope.off('mousedown touchstart', this.mousedown)
      this.stage.off('mouseup touchend', this.mouseup)
      this.stage.off('mousemove touchmove', this.mousemove)

      if (!enabled) return

      this.drawingScope.on('mousedown touchstart', this.mousedown)
      this.stage.on('mouseup touchend', this.mouseup)
      this.stage.on('mousemove touchmove', this.mousemove)
    },
    mousedown () {
      this.isPaint = true
      // ペンモード
      if (this.drawMode === 'pen') {
        this.context.lineWidth = this.consts.drawCanvasPenLineWidthPixel.sign
        this.context.globalCompositeOperation = 'source-over'
      }
      // 消しゴムモード
      if (this.drawMode === 'eraser') {
        this.context.lineWidth = this.consts.drawCanvasEraserLineWidthPixel
        this.context.globalCompositeOperation = 'destination-out'
      }
      this.lastPointerPosition = this.stage.getPointerPosition()
      this.$emit('sendSignalingDraw', this.stage.getPointerPosition(), true)
    },
    mouseup () {
      this.isPaint = false
    },
    mousemove () {
      if (!this.isPaint) return

      const p = this.stage.getPointerPosition()
      this.drawing(p)
      this.$emit('sendSignalingDraw', p, false)
    },
    syncDraw (signalingData) {
      if (signalingData.is_mousedown) {
        if (this.drawMode === 'pen') {
          this.context.lineWidth = this.consts.drawCanvasPenLineWidthPixel.sign
          this.context.globalCompositeOperation = 'source-over'
        }
        if (this.drawMode === 'eraser') {
          this.context.lineWidth = this.consts.drawCanvasEraserLineWidthPixel
          this.context.globalCompositeOperation = 'destination-out'
        }
        this.lastPointerPosition = signalingData.position
      }
      this.drawing(signalingData.position)
    },
    drawing (pointerPosition) {
      this.context.beginPath()
      this.localPos.x = this.lastPointerPosition.x - this.drawingScope.x()
      this.localPos.y = this.lastPointerPosition.y - this.drawingScope.y()
      this.context.moveTo(this.localPos.x, this.localPos.y)

      this.pos = pointerPosition
      this.localPos.x = this.pos.x - this.drawingScope.x()
      this.localPos.y = this.pos.y - this.drawingScope.y()
      this.context.lineTo(this.localPos.x, this.localPos.y)
      this.context.closePath()
      this.context.stroke()
      this.drawingLayer.draw()

      this.lastPointerPosition = this.pos
    },
    onClearCanvas () {
      this.context.globalCompositeOperation = 'destination-out'
      this.context.fillRect(0, 0, this.width, this.height)
      this.drawingLayer.draw()
    },
    onExportDrawnImage () {
      this.$emit('registDrawnImage', this.stage.toDataURL())
    },
    calculateWidth () {
      if (!this.stage) return
      const el = document.getElementById('content-area-inner')
      const computedStyle = window.getComputedStyle(el)
      let w =
        el.clientWidth -
        (parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight))
      w -= 2

      this.width = w
      const stageWidth = window.innerWidth >= 992 ? (w / 12) * 9 : w
      this.stage.width(stageWidth)
      this.stage.draw()
    }
  }
}
</script>
